import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { buildAdminPageDirectLink } from "../../../../utils/adminNavigationPaths";
import { fetchVehicleSources } from "../../../../services/vehicleSourceApi";
import {
  createAliasFromIssue,
  fetchVehicleNormalizationIssues,
  patchVehicleNormalizationIssue,
} from "../../../../services/vehicleNormalizationApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import VehicleIdentityReviewDetailPanel from "./VehicleIdentityReviewDetailPanel";

const WORKFLOW_STEPS = [
  {
    step: 1,
    title: "Triage open issues",
    detail: "Start with critical identity pollution and lookup failures that block catalog matching.",
  },
  {
    step: 2,
    title: "Review the raw vs normalized value",
    detail: "Open a case to see diagnostics, suggested corrections, and how many listings are affected.",
  },
  {
    step: 3,
    title: "Resolve or teach the catalog",
    detail: "Approve a correction, create an alias for recurring raw values, or ignore noise.",
  },
];

const QUEUE_TABS = [
  { value: "OPEN", label: "Needs attention", summaryKey: "open_issues" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "IGNORED", label: "Ignored" },
  { value: "", label: "All" },
];

const ISSUE_TYPE_LABELS = {
  IDENTITY_POLLUTION: "Identity pollution",
  LOOKUP_ISSUE: "Lookup issue",
  NO_LOOKUP_MATCH: "No lookup match",
  LOW_CONFIDENCE: "Low confidence",
  SUSPICIOUS_VALUE: "Suspicious value",
};

const FIELD_LABELS = {
  make: "Make",
  model: "Model",
  trim: "Trim",
  transmission: "Transmission",
  fuel_type: "Fuel type",
  body_type: "Body type",
  year: "Year",
  mileage: "Mileage",
};

const SEVERITY_CLASS = {
  critical: "is-critical",
  high: "is-high",
  medium: "is-medium",
  low: "is-low",
};

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatConfidence(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${Math.round(Number(value) * 100)}%`;
}

function formatFieldLabel(fieldName) {
  return FIELD_LABELS[fieldName] || fieldName || "—";
}

function formatPercent(part, total) {
  if (!total) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

function AliasDialog({ issue, onClose, onSubmit, submitting }) {
  const [targetKey, setTargetKey] = useState(issue?.normalizedAttempt || "");

  if (!issue) return null;

  return (
    <div className="admin-norm-issues__dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-norm-issues__dialog"
        role="dialog"
        aria-labelledby="alias-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-norm-issues__dialog-header">
          <div>
            <h2 id="alias-dialog-title">Create catalog alias</h2>
            <p className="muted">
              Map repeated raw value <strong>{issue.rawValue || "—"}</strong> to a normalized lookup key.
            </p>
          </div>
          <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
            Close
          </button>
        </header>
        <label className="admin-norm-issues__dialog-field">
          <span>Normalized lookup key</span>
          <input
            type="text"
            value={targetKey}
            onChange={(event) => setTargetKey(event.target.value)}
            placeholder="e.g. AUTOMATIC, GASOLINE, CX_30"
            autoFocus
          />
        </label>
        <p className="admin-norm-issues__dialog-hint muted">
          Related listings will be reprocessed after the alias is saved.
        </p>
        <div className="admin-norm-issues__dialog-actions">
          <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
            disabled={!targetKey.trim() || submitting}
            onClick={() => onSubmit(targetKey.trim())}
          >
            {submitting ? "Saving…" : "Create alias"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCards({ summary }) {
  const totalListings = summary?.total_listings ?? 0;
  const normalizedListings = summary?.normalized_listings ?? 0;
  const openIssues = summary?.open_issues ?? 0;
  const unresolvedListings = summary?.unresolved_listings ?? 0;
  const lowConfidence = summary?.low_confidence_listings ?? 0;

  return (
    <section className="admin-norm-issues__summary-grid">
      <article className="admin-norm-issues__summary-card">
        <p className="admin-norm-issues__summary-label">Catalog coverage</p>
        <p className="admin-norm-issues__summary-value">
          {normalizedListings.toLocaleString()}
          <span className="admin-norm-issues__summary-sub">
            / {totalListings.toLocaleString()} listings normalized
          </span>
        </p>
        <div className="admin-norm-issues__progress" aria-hidden="true">
          <span style={{ width: formatPercent(normalizedListings, totalListings) }} />
        </div>
        <p className="admin-norm-issues__summary-foot">
          {formatPercent(normalizedListings, totalListings)} of inventory mapped to canonical values
        </p>
      </article>
      <article className="admin-norm-issues__summary-card admin-norm-issues__summary-card--alert">
        <p className="admin-norm-issues__summary-label">Open issues</p>
        <p className="admin-norm-issues__summary-value">{openIssues.toLocaleString()}</p>
        <p className="admin-norm-issues__summary-foot">Cases waiting for operator review</p>
      </article>
      <article className="admin-norm-issues__summary-card">
        <p className="admin-norm-issues__summary-label">Unresolved listings</p>
        <p className="admin-norm-issues__summary-value">{unresolvedListings.toLocaleString()}</p>
        <p className="admin-norm-issues__summary-foot">Listings still missing a full identity match</p>
      </article>
      <article className="admin-norm-issues__summary-card">
        <p className="admin-norm-issues__summary-label">Low confidence</p>
        <p className="admin-norm-issues__summary-value">{lowConfidence.toLocaleString()}</p>
        <p className="admin-norm-issues__summary-foot">Matches below 55% confidence</p>
      </article>
    </section>
  );
}

function QueueTabs({ activeStatus, summary, onSelect }) {
  return (
    <div className="admin-norm-issues__queue" role="tablist" aria-label="Issue queue">
      {QUEUE_TABS.map((tab) => {
        const count =
          tab.summaryKey && summary ? summary[tab.summaryKey] ?? 0 : null;
        const isActive = activeStatus === tab.value;
        return (
          <button
            key={tab.value || "all"}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`admin-norm-issues__queue-tab${isActive ? " is-active" : ""}`}
            onClick={() => onSelect(tab.value)}
          >
            <span>{tab.label}</span>
            {count != null ? <span className="admin-norm-issues__queue-count">{count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

function IssueCard({ issue, canManage, onOpenDetail, onCreateAlias, onIgnore }) {
  const selectedIdentity = [issue.makeNormalized, issue.modelNormalized, issue.trimNormalized]
    .filter(Boolean)
    .join(" ");
  const issueLabel = issue.diagnosticMetadata?.label || ISSUE_TYPE_LABELS[issue.issueType] || issue.issueType;

  return (
    <article className="admin-norm-issues__card">
      <header className="admin-norm-issues__card-header">
        <div>
          <div className="admin-norm-issues__card-badges">
            <span
              className={`admin-norm-issues__severity ${SEVERITY_CLASS[issue.severity] || ""}`}
            >
              {issue.severity || "unknown"}
            </span>
            <span className="admin-norm-issues__chip">{formatFieldLabel(issue.fieldName)}</span>
            {issue.issueCategory ? (
              <span className="admin-norm-issues__chip admin-norm-issues__chip--muted">
                {issue.issueCategory.replace(/_/g, " ")}
              </span>
            ) : null}
          </div>
          <h3 className="admin-norm-issues__card-title">{issueLabel}</h3>
          <p className="admin-norm-issues__card-meta">
            {issue.sourceName || issue.sourceKey || "Unknown source"}
            <span aria-hidden="true"> · </span>
            Seen {issue.occurrenceCount ?? 1} time{(issue.occurrenceCount ?? 1) === 1 ? "" : "s"}
            <span aria-hidden="true"> · </span>
            Confidence {formatConfidence(issue.confidence)}
          </p>
        </div>
        <span className={`admin-norm-issues__status admin-norm-issues__status--${(issue.status || "open").toLowerCase()}`}>
          {issue.status || "OPEN"}
        </span>
      </header>

      <div className="admin-norm-issues__comparison">
        <div className="admin-norm-issues__comparison-row">
          <span className="admin-norm-issues__comparison-label">Raw value</span>
          <span className="admin-norm-issues__comparison-value">{issue.rawValue || "—"}</span>
        </div>
        <div className="admin-norm-issues__comparison-row">
          <span className="admin-norm-issues__comparison-label">Current match</span>
          <span className="admin-norm-issues__comparison-value">
            {issue.normalizedAttempt || selectedIdentity || "—"}
          </span>
        </div>
      </div>

      <footer className="admin-norm-issues__card-actions">
        <button type="button" className="admin-dashboard__action-btn" onClick={() => onOpenDetail(issue.id)}>
          Review case
        </button>
        <button
          type="button"
          className="admin-dashboard__action-btn"
          disabled={!canManage}
          onClick={() => onCreateAlias(issue)}
        >
          Create alias
        </button>
        <button
          type="button"
          className="admin-dashboard__action-btn"
          disabled={!canManage || issue.status !== "OPEN"}
          onClick={() => onIgnore(issue.id)}
        >
          Ignore
        </button>
        {issue.sourceUrl ? (
          <a
            href={issue.sourceUrl}
            className="admin-dashboard__action-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            Source listing
          </a>
        ) : null}
      </footer>
    </article>
  );
}

const VehicleNormalizationIssuesPage = ({ onClose }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [issues, setIssues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [aliasIssue, setAliasIssue] = useState(null);
  const [aliasSubmitting, setAliasSubmitting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [fieldName, setFieldName] = useState("");
  const [issueType, setIssueType] = useState("");
  const [status, setStatus] = useState("OPEN");
  const [search, setSearch] = useState("");
  const [vehicleSourceId, setVehicleSourceId] = useState("");
  const [issueCategory, setIssueCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [sources, setSources] = useState([]);

  const pageLink = useMemo(
    () => buildAdminPageDirectLink("vehicle-normalization-issues"),
    []
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchVehicleNormalizationIssues({
        fieldName: fieldName || undefined,
        issueType: issueType || undefined,
        status: status || undefined,
        search: search.trim() || undefined,
        vehicleSourceId: vehicleSourceId || undefined,
        issueCategory: issueCategory || undefined,
        severity: severity || undefined,
        limit: 200,
      });
      setIssues(data.issues);
      setSummary(data.summary);
    } catch (error) {
      setIssues([]);
      setErrorMessage(error.message || "Unable to load normalization issues.");
    } finally {
      setLoading(false);
    }
  }, [fieldName, issueType, status, search, vehicleSourceId, issueCategory, severity]);

  useEffect(() => {
    fetchVehicleSources({ sort: "source_name", sortDir: "asc" })
      .then((data) => setSources(data.sources || []))
      .catch(() => setSources([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const handleIgnore = async (id) => {
    if (!canManage) return;
    try {
      await patchVehicleNormalizationIssue(id, { status: "IGNORED" });
      setToast("Issue ignored.");
      await load();
    } catch (error) {
      setToast(error.message || "Update failed.");
    }
  };

  const handleCreateAlias = async (normalizedValue) => {
    if (!canManage || !aliasIssue) return;
    try {
      setAliasSubmitting(true);
      await createAliasFromIssue(aliasIssue.id, { normalizedValue });
      setToast("Alias created and related listings reprocessed.");
      setAliasIssue(null);
      await load();
    } catch (error) {
      setToast(error.message || "Alias creation failed.");
    } finally {
      setAliasSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!pageLink) return;
    try {
      await navigator.clipboard.writeText(pageLink);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setToast("Unable to copy link.");
    }
  };

  const handleFieldChipClick = (nextField) => {
    setFieldName((current) => (current === nextField ? "" : nextField));
  };

  return (
    <div className="admin-dashboard admin-norm-issues admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <p className="admin-dashboard__eyebrow">Vehicle Market · Data quality</p>
          <h1 className="admin-dashboard__title">Normalization Issues</h1>
          <p className="admin-dashboard__subtitle">
            Review identity pollution, lookup gaps, and low-confidence matches across imported vehicle listings.
          </p>
          {pageLink ? (
            <p className="admin-norm-issues__page-link muted">
              Direct link: <code>{pageLink.replace(/^https?:\/\/[^/]+/, "")}</code>
            </p>
          ) : null}
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="admin-dashboard__action-btn" onClick={handleCopyLink}>
            {linkCopied ? "Link copied" : "Copy page link"}
          </button>
          <button type="button" className="admin-dashboard__action-btn" onClick={load}>
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      <section className="admin-norm-issues__workflow panel-card">
        <h2 className="admin-norm-issues__section-title">How to work this queue</h2>
        <ol className="admin-norm-issues__workflow-steps">
          {WORKFLOW_STEPS.map((step) => (
            <li key={step.step}>
              <span className="admin-norm-issues__workflow-number">{step.step}</span>
              <div>
                <strong>{step.title}</strong>
                <p>{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {summary ? <SummaryCards summary={summary} /> : null}

      <section className="admin-norm-issues__controls panel-card">
        <div className="admin-norm-issues__controls-top">
          <QueueTabs activeStatus={status} summary={summary} onSelect={setStatus} />
          <input
            type="search"
            className="admin-norm-issues__search"
            placeholder="Search raw value, make, or model"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {summary?.issuesByField?.length ? (
          <div className="admin-norm-issues__field-chips">
            <span className="admin-norm-issues__field-chips-label">Open issues by field</span>
            {summary.issuesByField.map((row) => (
              <button
                key={row.field_name}
                type="button"
                className={`admin-norm-issues__chip admin-norm-issues__chip--button${
                  fieldName === row.field_name ? " is-active" : ""
                }`}
                onClick={() => handleFieldChipClick(row.field_name)}
              >
                {formatFieldLabel(row.field_name)} ({row.count})
              </button>
            ))}
          </div>
        ) : null}

        <button
          type="button"
          className="admin-norm-issues__advanced-toggle"
          onClick={() => setShowAdvancedFilters((current) => !current)}
          aria-expanded={showAdvancedFilters}
        >
          {showAdvancedFilters ? "Hide advanced filters" : "Show advanced filters"}
        </button>

        {showAdvancedFilters ? (
          <div className="admin-norm-issues__advanced-filters">
            <select value={vehicleSourceId} onChange={(event) => setVehicleSourceId(event.target.value)}>
              <option value="">All sources</option>
              {sources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.sourceName}
                </option>
              ))}
            </select>
            <select value={issueType} onChange={(event) => setIssueType(event.target.value)}>
              <option value="">All issue types</option>
              {Object.entries(ISSUE_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select value={issueCategory} onChange={(event) => setIssueCategory(event.target.value)}>
              <option value="">All categories</option>
              <option value="title_pollution">Title pollution</option>
              <option value="identity_ambiguity">Identity ambiguity</option>
              <option value="lookup_not_found">Lookup not found</option>
              <option value="auto_corrected">Auto-corrected</option>
              <option value="low_confidence">Low confidence</option>
            </select>
            <select value={severity} onChange={(event) => setSeverity(event.target.value)}>
              <option value="">All severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        ) : null}
      </section>

      {toast ? <p className="admin-norm-issues__toast">{toast}</p> : null}
      {loading ? <AdminStatePanel variant="loading" message="Loading issues…" /> : null}
      {!loading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={load} />
      ) : null}

      {selectedIssueId ? (
        <VehicleIdentityReviewDetailPanel
          issueId={selectedIssueId}
          canManage={canManage}
          onClose={() => setSelectedIssueId(null)}
          onUpdated={load}
        />
      ) : null}

      {!loading && !errorMessage ? (
        <section className="admin-norm-issues__list">
          {issues.length === 0 ? (
            <div className="admin-norm-issues__empty panel-card">
              <h2>No issues in this queue</h2>
              <p className="muted">
                {status === "OPEN"
                  ? "Nothing needs attention right now. Try another queue tab or clear your filters."
                  : "No normalization issues match the current filters."}
              </p>
            </div>
          ) : (
            issues.map((issue) => (
              <IssueCard
                key={issue.id}
                issue={issue}
                canManage={canManage}
                onOpenDetail={setSelectedIssueId}
                onCreateAlias={setAliasIssue}
                onIgnore={handleIgnore}
              />
            ))
          )}
        </section>
      ) : null}

      <AliasDialog
        issue={aliasIssue}
        onClose={() => setAliasIssue(null)}
        onSubmit={handleCreateAlias}
        submitting={aliasSubmitting}
      />
    </div>
  );
};

export default VehicleNormalizationIssuesPage;
